import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { delay, map, withLatestFrom } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

import { stopPropagation, unsubscribe } from '@fcom/core/utils';
import { FinnairBoundFareFamily } from '@fcom/dapi/api/models';
import { DimensionsAndDetails, ExtendedFareFamily } from '@fcom/common-booking';
import { ScrollHandleContainerComponent } from '@fcom/common/components/scroll-handle-container/scroll-handle-container.component';
import { GaContext } from '@fcom/common/interfaces';
import { Direction } from '@fcom/common/interfaces/utils.interface';
import { LocationRouteType } from '@fcom/core-api/interfaces';
import { NotificationTheme } from '@fcom/ui-components';

import { DimensionsAndDetailsModalService } from '../../services/dimensions-and-details-modal.service';
import { CabinClassSelectionFareFamily } from '../../interfaces';
import { effectiveCabinClass, filterAndSortFareFamilyByCabinClass } from '../../utils/offer.utils';

@Component({
  selector: 'fin-offer-farefamilies',
  templateUrl: 'offer-farefamilies.component.html',
  styleUrls: ['./offer-farefamilies.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OfferFarefamiliesComponent implements OnInit, OnDestroy, AfterViewChecked {
  readonly GaContext = GaContext;
  readonly NotificationTheme = NotificationTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  @Input() bound$: Observable<any>;
  @Input() selectedBoundId$: Observable<string>;
  @Input() selectedFareFamilyCode$: Observable<string>;
  @Input() currencyCode$: Observable<string>;
  @Input() isMobile$: Observable<boolean>;
  @Input() isShortHaul$: Observable<boolean>;
  @Input() routeType$: Observable<LocationRouteType>;

  @Output()
  selectFareFamily: EventEmitter<FinnairBoundFareFamily> = new EventEmitter();
  @Output()
  showBenefits: EventEmitter<DimensionsAndDetails> = new EventEmitter();
  @Output()
  scrollBenefits: EventEmitter<Direction> = new EventEmitter();
  @Output()
  showCabinClassFareFamilies: EventEmitter<string> = new EventEmitter();

  isMobile: boolean;
  optionName = `fare-family-option-${uuid()}`;
  accordionId = `benefit-accordion-${uuid()}`;
  showFareFamilyOptions = false;
  extendedFareFamilies$: Observable<ExtendedFareFamily[]>;
  extendedFareFamilies: ExtendedFareFamily[];
  cheapestOrSelectedFareFamilyOfEachCabinClass$: Observable<CabinClassSelectionFareFamily[]>;
  hasSelected = false;
  selectedCabinClass: string;

  private scrollRequest$: Subject<FinnairBoundFareFamily> = new Subject<FinnairBoundFareFamily>();
  private subscription = new Subscription();

  @ViewChild('scrollContainer', { static: false }) scrollContainer: ScrollHandleContainerComponent;
  @ViewChild('activeCabinClassHeaderContainer', { static: false, read: ElementRef })
  activeCabinClassHeaderContainer: ElementRef;
  @ViewChild('scrollHandleLeft', { static: false, read: ElementRef }) scrollHandleLeft: ElementRef;
  @ViewChild('scrollHandleRight', { static: false, read: ElementRef }) scrollHandleRight: ElementRef;
  @ViewChildren('fareFamilyOption', { read: ElementRef }) fareFamilyOptions: QueryList<ElementRef>;

  constructor(private dimensionsAndDetailsModalService: DimensionsAndDetailsModalService) {}

  ngOnInit(): void {
    this.cheapestOrSelectedFareFamilyOfEachCabinClass$ = combineLatest([
      this.bound$,
      this.selectedFareFamilyCode$,
      this.selectedBoundId$,
    ]).pipe(
      map(
        ([bound, selectedFareFamilyCode, selectedBoundId]) =>
          filterAndSortFareFamilyByCabinClass(bound.fareFamilies).map((f) => ({
            ...f,
            cabinClass: effectiveCabinClass(f),
            selected: f.fareFamilyCode === selectedFareFamilyCode && bound.boundId === selectedBoundId,
          })) as any[]
      )
    );

    this.extendedFareFamilies$ = combineLatest([this.bound$, this.selectedFareFamilyCode$, this.selectedBoundId$]).pipe(
      map(([bound, selectedFareFamilyCode, selectedBoundId]) =>
        bound.fareFamilies.map(
          (fareFamily): ExtendedFareFamily => ({
            ...fareFamily,
            benefits: fareFamily,
            selected: fareFamily.fareFamilyCode === selectedFareFamilyCode && bound.boundId === selectedBoundId,
          })
        )
      )
    );

    this.subscription.add(
      this.scrollRequest$
        .pipe(withLatestFrom(this.extendedFareFamilies$), delay(0))
        .subscribe(([fareFamily, fareFamilies]) => {
          const fareFamilyIndex = fareFamilies.findIndex((f) => f.id === fareFamily.id);
          this.scrollToCardByIndex(fareFamilyIndex);
        })
    );

    this.subscription.add(
      this.extendedFareFamilies$.subscribe((val) => {
        this.extendedFareFamilies = val;
      })
    );

    this.subscription.add(
      this.selectedFareFamilyCode$.subscribe((fareFamilyCode) => {
        this.hasSelected = !!fareFamilyCode;
      })
    );

    this.subscription.add(this.isMobile$.subscribe((isMobile) => (this.isMobile = isMobile)));
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
  }

  ngAfterViewChecked(): void {
    this.adjustFareFamilyOptionHeight();
  }

  adjustFareFamilyOptionHeight(): void {
    if (this.scrollContainer && this.isMobile$) {
      const children: HTMLElement[] = this.scrollContainer.container.nativeElement.querySelectorAll(
        'fin-fare-family-option .brand-name-description'
      );

      const itemHeights = Array.from(children).map((e) => e.getBoundingClientRect().height);
      const maxHeight = itemHeights.reduce((prev, curr) => (curr > prev ? curr : prev), 0);

      Array.from(children)
        .filter((e) => e.style.flexBasis !== `${maxHeight}px`)
        .forEach((e) => {
          e.style.flexBasis = `${maxHeight}px`;
        });
    }
  }

  trackByFn(index: number): number {
    return index;
  }

  showFareFamiliesAndScrollToCabinClass(event: Event, fareFamily: CabinClassSelectionFareFamily): void {
    stopPropagation(event);

    this.showCabinClassFareFamilies.emit(fareFamily.cabinClass);

    this.selectedCabinClass = fareFamily.cabinClass;

    if (!this.hasSelected) {
      this.selectFareFamily.emit(fareFamily);
    }

    this.showFareFamilyOptions = true;

    setTimeout(() => {
      this.activeCabinClassHeaderContainer?.nativeElement?.focus();
    }, 0);
    this.scrollToFareFamily(fareFamily.preSelectFareFamily || fareFamily);
  }

  /**
   * Displays card center based on container
   * @param index, Card index to display centered
   */
  scrollToCardByIndex(index: number): void {
    this.scrollContainer.scrollToElement(this.fareFamilyOptions.toArray()[index]);
  }

  private scrollToFareFamily(fareFamily: CabinClassSelectionFareFamily | FinnairBoundFareFamily): void {
    this.scrollRequest$.next(fareFamily);
  }

  hideFareFamilies(event: Event): void {
    stopPropagation(event);

    this.showFareFamilyOptions = false;
  }

  handleShowBenefits(): void {
    this.dimensionsAndDetailsModalService.opedAllDimensionsAndDetails();
    this.showBenefits.emit({
      extendedFareFamilies: this.extendedFareFamilies,
      cabinClass: this.selectedCabinClass || 'ECONOMY',
    });
  }

  handleSelectFareFamily(boundFareFamily: FinnairBoundFareFamily): void {
    this.selectFareFamily.emit(boundFareFamily);
  }
}
